import { assertUnreachable } from "@/utils";
import {
  Action,
  CustomAction,
  LedAction,
  MouseAction,
} from "@/keyboard/keyboard_config";
import {
  ActNoOp,
  ActTrans,
  ActKeyCode,
  ActMultipleKeyCodes,
  ActMultipleActions,
  ActLayer,
  ActDefaultLayer,
  ActHoldTap,
  ActCustom,
  ActCustomLed,
  ActCustomMouse,
  ActCustomConsumer,
  ActCustomFirmware,
  ActCustomLedCycle,
  ActCustomLedBrightness,
  ActCustomMouseClick,
  ActCustomMouseMove,
  ActCustomMouseSensitivity,
} from "@/keyboard/action";
import {
  ActionInternal,
  ActionInternalNested,
} from "@/keyboard/internal_config";

// Type guards for action variants
const has = (a: unknown, prop: string): boolean =>
  typeof a == "object" && a != null && prop in a;

type Act = Action | ActionInternal;
export const isNoOp = (a: Act): a is ActNoOp => a == "NoOp";
export const isTrans = (a: Act | NonKeycodeAction): a is ActTrans =>
  a == "Trans";
export const isKeyCode = (a: Act): a is ActKeyCode => has(a, "KeyCode");
export const isMultipleKeyCodes = (a: Act): a is ActMultipleKeyCodes =>
  has(a, "MultipleKeyCodes");
export const isMultipleActions = (a: Action): a is ActMultipleActions =>
  has(a, "MultipleActions");
export const isLayer = (a: Act | NonKeycodeAction): a is ActLayer =>
  has(a, "Layer");
export const isDefaultLayer = (
  a: Act | NonKeycodeAction
): a is ActDefaultLayer => has(a, "DefaultLayer");
export const isHoldTap = (a: Action): a is ActHoldTap => has(a, "HoldTap");
export const isCustom = (a: Act): a is ActCustom => has(a, "Custom");

export const isCustomLed = (a: CustomAction): a is ActCustomLed =>
  has(a, "Led");
export const isCustomMouse = (a: CustomAction): a is ActCustomMouse =>
  has(a, "Mouse");
export const isCustomFirmware = (
  a: CustomAction | NonKeycodeAction
): a is ActCustomFirmware => has(a, "Firmware");
export const isCustomConsumer = (
  a: CustomAction | NonKeycodeAction
): a is ActCustomConsumer => has(a, "Consumer");
export const isCustomLedCycle = (
  a: LedAction | NonKeycodeAction
): a is ActCustomLedCycle => has(a, "Cycle");
export const isCustomLedBrightness = (
  a: LedAction | NonKeycodeAction
): a is ActCustomLedBrightness => has(a, "Brightness");
export const isCustomMouseClick = (
  a: MouseAction | NonKeycodeAction
): a is ActCustomMouseClick => has(a, "Click");
export const isCustomMouseMove = (
  a: MouseAction | NonKeycodeAction
): a is ActCustomMouseMove => has(a, "Move");
export const isCustomMouseSensitivity = (
  a: MouseAction | NonKeycodeAction
): a is ActCustomMouseSensitivity => has(a, "Sensitivity");

// Helper type for action menu
export type ActCustomFlat =
  | ActCustomConsumer
  | ActCustomFirmware
  | ActCustomLedCycle
  | ActCustomLedBrightness
  | ActCustomMouseClick
  | ActCustomMouseMove
  | ActCustomMouseSensitivity;
export type NonKeycodeAction =
  | ActTrans
  | ActLayer
  | ActDefaultLayer
  | ActCustomFlat;

// Type guards for NonKeycodeAction
export const isCustomFlat = (a: NonKeycodeAction): a is ActCustomFlat =>
  isCustomConsumer(a) ||
  isCustomFirmware(a) ||
  isCustomLedCycle(a) ||
  isCustomLedBrightness(a) ||
  isCustomMouseClick(a) ||
  isCustomMouseMove(a) ||
  isCustomMouseSensitivity(a);

// Array to tuple: https://stackoverflow.com/a/45486495
export const ACTIONS = [
  "NoOp",
  "Trans",
  "KeyCode",
  "MultipleKeyCodes",
  "MultipleActions",
  "Layer",
  "DefaultLayer",
  "HoldTap",
  "Custom",
] as const;
type ActionsTuple = typeof ACTIONS;
export type ActionType = ActionsTuple[number];

export const actionType = (a: Act): ActionType =>
  isNoOp(a)
    ? "NoOp"
    : isTrans(a)
    ? "Trans"
    : isKeyCode(a)
    ? "KeyCode"
    : isMultipleKeyCodes(a)
    ? "MultipleKeyCodes"
    : isMultipleActions(a as Action) // safe because we just check the top-level key
    ? "MultipleActions"
    : isLayer(a)
    ? "Layer"
    : isDefaultLayer(a)
    ? "DefaultLayer"
    : isHoldTap(a as Action) // safe because we just check the top-level key
    ? "HoldTap"
    : "Custom";

export const flattenCustomAction = (act: ActCustom): ActCustomFlat => {
  const custom = act.Custom;
  if (isCustomLed(custom)) return custom.Led;
  else if (isCustomMouse(custom)) return custom.Mouse;
  else if (isCustomConsumer(custom) || isCustomFirmware(custom)) return custom;
  else return assertUnreachable(custom);
};

export const unflattenCustomAction = (act: ActCustomFlat): ActCustom => {
  if (isCustomLedCycle(act) || isCustomLedBrightness(act))
    return { Custom: { Led: act } };
  if (
    isCustomMouseClick(act) ||
    isCustomMouseMove(act) ||
    isCustomMouseSensitivity(act)
  )
    return { Custom: { Mouse: act } };
  if (isCustomConsumer(act) || isCustomFirmware(act)) return { Custom: act };
  return assertUnreachable(act);
};

export const unflattenNonkeycodeAction = (
  act: NonKeycodeAction
): ActionInternalNested => {
  if (isCustomFlat(act)) return unflattenCustomAction(act);
  return act;
};

export const defaultAction = (typ: ActionType): ActionInternal => {
  switch (typ) {
    case "NoOp":
      return "NoOp";
    case "Trans":
      return "Trans";
    case "KeyCode":
      return { KeyCode: "A" };
    case "MultipleKeyCodes":
      return { MultipleKeyCodes: ["A"] };
    case "MultipleActions":
      return { MultipleActions: ["NoOp"] };
    case "Layer":
      return { Layer: 1 };
    case "DefaultLayer":
      return { DefaultLayer: 1 };
    case "HoldTap":
      return {
        HoldTap: {
          hold: { KeyCode: "Space" },
          tap: { Layer: 1 },
        },
      };
    case "Custom":
      return { Custom: { Mouse: { Click: "Left" } } };
  }
};
